import { Button } from './Button'
import { Text } from './Text'
import { useI18n } from '../i18n'
import { usePwaInstall } from '../hooks/pwaInstall'

/**
 * Dismissible "install the app" banner. Renders only when all three hold:
 *   - a deferred install prompt is available (`canInstall`),
 *   - the app isn't already installed (see `isRunningStandalone`), and
 *   - the user hasn't closed the banner this browser session.
 *
 * Dismissal is session-scoped, so closing it means "not right now" — it comes
 * back on the next visit rather than being gone forever.
 *
 * Mount this from the login screen onward — never inside the pre-qualification
 * flow — so the install prompt is only ever offered after pre-qual. Returns null
 * when there's nothing to offer, so it's safe to drop into a layout unguarded.
 */
export function InstallPrompt({ className }: { className?: string }) {
  const { canInstall, installed, dismissed, dismiss, promptInstall } = usePwaInstall()
  const { t } = useI18n()

  if (!canInstall || installed || dismissed) return null

  return (
    <div className={className}>
      <div className="flex items-center gap-3 rounded-sm bg-gray-900 py-2.5 pl-3 pr-2 text-white">
        {/* App mark in a tinted tile, echoing the launcher icon the install produces. */}
        <span className="flex size-10 shrink-0 items-center justify-center rounded-[10px] bg-white/10">
          <img src="/pwa-64x64.png" alt="" aria-hidden="true" className="size-7" />
        </span>

        <div className="min-w-0 flex-1">
          <Text as="p" variant="caption" weight="bold" className="uppercase">
            {t('pwa.install.banner.title')}
          </Text>
          <Text as="p" variant="xs" className="text-white/70">
            {t('pwa.install.banner.body')}
          </Text>
        </div>

        <Button size="sm" className="shrink-0 gap-1.5" onClick={() => void promptInstall()}>
          {/* Download glyph (tray + arrow), inline so it follows the button's text color. */}
          <svg
            viewBox="0 0 24 24"
            className="size-4"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
            aria-hidden="true"
          >
            <path d="M12 3v12m0 0 4-4m-4 4-4-4" />
            <path d="M4 17v2a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-2" />
          </svg>
          {t('pwa.install.cta')}
        </Button>

        <button
          type="button"
          onClick={dismiss}
          aria-label={t('pwa.install.dismiss')}
          className="flex size-8 shrink-0 items-center justify-center rounded-sm text-white/70 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white focus-visible:ring-offset-2 focus-visible:ring-offset-gray-900"
        >
          <svg
            viewBox="0 0 24 24"
            className="size-4"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            aria-hidden="true"
          >
            <path d="M6 6l12 12M18 6L6 18" />
          </svg>
        </button>
      </div>
    </div>
  )
}
